// formatNumber.js
// Ref. https://stackoverflow.com/a/17633552
'use strict';

const RANGES = [
  { divider: 1e18, suffix: 'E' },
  { divider: 1e15, suffix: 'P' },
  { divider: 1e12, suffix: 'T' },
  { divider: 1e9, suffix: 'G' },
  { divider: 1e6, suffix: 'M' },
  { divider: 1e3, suffix: 'k' },
];

/**
 * 將數字格式化為帶有單位後綴的字串，保留一位小數
 * @param {number} num - 要格式化的數字
 * @param {Object} options - 選項配置
 * @param {string} options.maxSuffix - 限制使用的最大單位後綴
 * @returns {string} 格式化後的字串
 *
 * @example
 * formatNumber(500)           // '500'
 * formatNumber(1000)          // '1k'
 * formatNumber(1500)          // '1.5k'
 * formatNumber(1500000)       // '1.5M'
 * formatNumber(-1500)         // '-1.5k'
 * formatNumber(5000000, { maxSuffix: 'k' })  // '5000k'
 */
const formatNumber = (num, options = {}) => {
  const { maxSuffix } = options;

  if (num < 0) {
    return `-${formatNumber(-num, options)}`;
  }

  for (let i = 0; i < RANGES.length; i++) {
    if (num >= RANGES[i].divider) {
      if (maxSuffix) {
        const maxIndex = RANGES.findIndex(range => range.suffix === maxSuffix);
        if (maxIndex !== -1 && i < maxIndex) {
          continue;
        }
      }

      return (
        (num / RANGES[i].divider).toFixed(1).toString().replace(/\.0$/, '') +
        RANGES[i].suffix
      );
    }
  }

  return num.toString();
};

const COMPACT_UNITS = [
  { value: 1_000_000_000_000_000_000, symbol: 'E' },
  { value: 1_000_000_000_000_000, symbol: 'P' },
  { value: 1_000_000_000_000, symbol: 'T' },
  { value: 1_000_000_000, symbol: 'B' },
  { value: 1_000_000, symbol: 'M' },
  { value: 1_000, symbol: 'K' },
];

/**
 * 將數字格式化為緊湊的整數+單位格式
 * 此方法適合用於顯示約略數字（如評分數量、觀看次數等）
 * @param {number} num - 要格式化的數字
 * @returns {string} 格式化後的字串，無效輸入返回空字串
 *
 * @example
 * formatNumberCompact(500)        // '500'
 * formatNumberCompact(999)        // '999'
 * formatNumberCompact(1000)       // '1K'
 * formatNumberCompact(1500)       // '1K'
 * formatNumberCompact(10000)      // '10K'
 * formatNumberCompact(1500000)    // '1M'
 * formatNumberCompact(2000000)    // '2M'
 * formatNumberCompact(-1500)      // '-1K'
 * formatNumberCompact(-2000)      // '-2K'
 * formatNumberCompact(NaN)        // ''
 * formatNumberCompact('invalid')  // ''
 */
export const formatNumberCompact = num => {
  if (typeof num !== 'number' || isNaN(num)) return '';

  // 處理負數
  if (num < 0) {
    const formatted = formatNumberCompact(-num);
    return formatted ? `-${formatted}` : '';
  }

  // 小於 1000 直接回傳
  if (num < 1000) {
    return num.toString();
  }

  // 由大到小比對符合的單位
  for (const { value, symbol } of COMPACT_UNITS) {
    if (num >= value) {
      const base = Math.floor(num / value);
      return `${base}${symbol}`;
    }
  }

  return num.toString();
};

export default formatNumber;
